








































































































import { Component, Vue, Ref, Watch } from "vue-property-decorator";
import {
  ContactPersonInfo,
  ProjectDetails,
} from "@/data/models/ProjectDetails";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { ButtonState } from "@/components/common/StateButton.vue";
import clone from "clone";
import { getTargetAppVersion } from "@/data/repository/ClientRepository";

@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class ContactPersonSectionComponent extends Vue {
  showTitleInput = getTargetAppVersion() >= 1064;

  @Ref("baseDialog")
  baseDialogComponent!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  resolve?: (value: ContactPersonInfo | undefined) => void = undefined;
  reject?: (reason: string) => void = undefined;

  contactPerson: ContactPersonInfo = {
    id: "",
    contactPersonNumber: 1,
  };

  projectDetails!: ProjectDetails;
  buttonState = ButtonState.RED;
  isEditMode = false;
  title = "";

  private snapshotTitle = "";
  private snapshotFirstName = "";
  private snapshotLastName = "";
  private snapshotEmail = "";
  private snapshotFloor = "";
  private snapshotPhone = "";
  private snapshotMobilePhone = "";
  private snapshotNote = "";

  get copyButtonTitle() {
    return this.$t(
      this.projectDetails.clientData.directOrder
        ? "clientData.copyClient"
        : "clientData.copyPolicyHolder"
    ).toString();
  }

  hasInitialData(): boolean {
    const c = this.contactPerson;
    return !!(c.title || c.firstName || c.lastName || c.emailAddress || c.floor || c.phone || c.mobilePhone || c.note);
  }

  takeSnapshot(): void {
    const c = this.contactPerson;
    this.snapshotTitle = c.title ?? "";
    this.snapshotFirstName = c.firstName ?? "";
    this.snapshotLastName = c.lastName ?? "";
    this.snapshotEmail = c.emailAddress ?? "";
    this.snapshotFloor = c.floor ?? "";
    this.snapshotPhone = c.phone ?? "";
    this.snapshotMobilePhone = c.mobilePhone ?? "";
    this.snapshotNote = c.note ?? "";
  }

  get hasChanged(): boolean {
    const c = this.contactPerson;
    return (c.title ?? "") !== this.snapshotTitle
      || (c.firstName ?? "") !== this.snapshotFirstName
      || (c.lastName ?? "") !== this.snapshotLastName
      || (c.emailAddress ?? "") !== this.snapshotEmail
      || (c.floor ?? "") !== this.snapshotFloor
      || (c.phone ?? "") !== this.snapshotPhone
      || (c.mobilePhone ?? "") !== this.snapshotMobilePhone
      || (c.note ?? "") !== this.snapshotNote;
  }

  get displayTitle(): string {
    return this.isEditMode ? `${this.title} ${this.$t('common.edit').toString().toLowerCase()}` : this.title;
  }

  @Watch("hasChanged")
  onHasChangedUpdate() {
    this.baseDialogComponent.negativeButtonText = this.hasChanged
      ? this.$t("common.abort").toString()
      : this.$t("common.close").toString();
  }

  async show(contactPerson: ContactPersonInfo, projectDetails: ProjectDetails) {
    this.contactPerson = contactPerson;
    this.projectDetails = projectDetails;
    this.title = this.$t("clientData.contactPerson").toString();

    this.isEditMode = !this.hasInitialData();
    if (this.isEditMode) {
      this.takeSnapshot();
    }

    this.baseDialogComponent.show({
      title: this.displayTitle,
      positiveButtonText: this.isEditMode
        ? this.$t("common.save").toString()
        : this.$t("settings.buttons.change").toString(),
      persistent: this.isEditMode,
      closeOnSave: this.isEditMode,
    });

    return new Promise<ContactPersonInfo | undefined>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  onPositiveAction() {
    if (!this.isEditMode) {
      this.isEditMode = true;
      this.takeSnapshot();
      this.baseDialogComponent.title = this.displayTitle;
      this.baseDialogComponent.positiveButtonText = this.$t("common.save").toString();
      this.baseDialogComponent.persistent = true;
      this.baseDialogComponent.closeOnSave = true;
      return;
    }
    this.resolve && this.resolve(this.contactPerson);
  }

  async onNegativeAction() {
    if (this.isEditMode && this.hasChanged) {
      const result = await this.confirmDialog.show({
        title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
        message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
        rejectMessage: this.$t("common.back").toString(),
        acceptMessage: this.$t("common.abort").toString(),
      });
      if (!result) return;
    }
    this.isEditMode = false;
    this.baseDialogComponent.isShowing = false;
    this.resolve && this.resolve(undefined);
  }

  onCancel() {
    if (this.isEditMode) return;
    this.resolve && this.resolve(undefined);
  }

  async onCopy() {
    const policyHolderInfo = this.projectDetails.clientData.policyHolderInfo;

    this.contactPerson.firstName = policyHolderInfo.firstName;
    this.contactPerson.lastName = policyHolderInfo.lastName;
    this.contactPerson.emailAddress = policyHolderInfo.emailAddress;
    this.contactPerson.phone = policyHolderInfo.phone;
    this.contactPerson.mobilePhone = policyHolderInfo.mobilePhone;

    this.contactPerson = clone(this.contactPerson);
  }
}
